import React, { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import Link from '@mui/material/Link';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { bgGradient } from 'src/theme/css';

const StyledInput = styled((props) => <TextField fullWidth {...props} />)(({ theme }) => ({
  [`& .${inputBaseClasses.input}`]: {
    color: theme.palette.common.white,
  },
  [`& .${inputLabelClasses.root}.${inputLabelClasses.shrink}`]: {
    color: theme.palette.grey[500],
    [`&.${inputLabelClasses.focused}`]: {
      color: theme.palette.grey[500],
    },
  },
}));

export default function MarketingLandingFreeSEO() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null); // reCAPTCHA için state ekliyoruz
  const [message, setMessage] = useState(''); // Mesaj state'i
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Buton disable state'i

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // reCAPTCHA token güncelleme
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async () => {
    if (!recaptchaToken) {
      setMessage('Bitte bestätigen Sie reCAPTCHA.');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    try {
      await axios.post('https://console.zebra360.de/api/mail', {
        ...formData,
        recaptchaToken, // reCAPTCHA token'ı ekliyoruz
      });
      
      // Başarılı mesaj ve buton disable
      setMessage('Anfrage wurde erfolgreich gesendet!');
      setIsButtonDisabled(true);

      // Mesajı 5 saniye sonra kaldır
      setTimeout(() => {
        setMessage('');
      }, 5000);
    } catch (error) {
      setMessage('Fehler beim Senden der Anfrage.');
      setTimeout(() => setMessage(''), 5000);
    }
  };

  return (
    <Box
      sx={{
        ...bgGradient({
          color: alpha(theme.palette.grey[900], 0),
          imgUrl: '/assets/images/marketing/marketing_get_free_seo.jpg',
        }),
        overflow: 'hidden',
        py: { xs: 10, md: 15 },
      }}
    >
      <Container>
        <Grid container spacing={{ xs: 5, md: 3 }} justifyContent="space-between">
          <Grid xs={12} md={5}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                background: theme.palette.primary.gradient,
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: { xs: 3, md: 8 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Jetzt Kostenlose 
              <br />Analyse erhalten
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ color: 'common.white', mb: 2 }}>
              <Typography color="inherit" variant="body">
                Entfaltet Deine Webseite und Dein Brand ihr volles Potenzial?
              </Typography>
            </Stack>
          </Grid>

          <Grid xs={12} md={5}>
            <Stack alignItems={{ xs: 'center', md: 'flex-start' }}>
              <StyledInput label="Name" name="name" value={formData.name} onChange={handleInputChange} sx={{ mb: 2.5 }} />
              <StyledInput label="Email" name="email" value={formData.email} onChange={handleInputChange} sx={{ mb: 2.5 }} />
              <StyledInput label="Phone" name="phone" value={formData.phone} onChange={handleInputChange} sx={{ mb: 2.5 }} />
              <StyledInput label="Website URL" name="website" value={formData.website} onChange={handleInputChange} sx={{ mb: 5 }} />
              
              {/* reCAPTCHA ekliyoruz */}
              <ReCAPTCHA
                sitekey="6LfEXUsqAAAAAER2-1fmpYjpYbiNaiAsIM68B86Y" // Google reCAPTCHA site key'inizi buraya ekleyin
                onChange={handleRecaptchaChange}
              />
              
              {/* Başarı/başarısızlık mesajı */}
              {message && (
                <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                  {message}
                </Typography>
              )}
              
              <Button
                size="large"
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={handleSubmit}
                disabled={isButtonDisabled} // Buton gönderim sonrası disable olacak
              >
                Jetzt Anfragen
              </Button>

              <Typography variant="caption" align="center" sx={{ color: 'text.secondary', mt: 3 }}>
              {`Mit dem Absenden dieses Formulars akzeptierst Du unsere `}
              <Link color="text.secondary" href="/agentur/datenschutz" underline="always">
                Datenschutzbedingungen
              </Link>
              {`.`}
            </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
